"use strict";
/**
 * Utility to extract dominant colors from images for creating gradient backgrounds
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createSubtleGradientFromPalette = exports.createGradientFromPalette = exports.extractColorsFromImage = void 0;
/**
 * Extract dominant colors from an image URL
 * Cross-platform implementation with web/mobile fallbacks
 */
const extractColorsFromImage = (imageUrl) => __awaiter(void 0, void 0, void 0, function* () {
    // Check if we're in a browser environment
    if (typeof document === 'undefined' || typeof Image === 'undefined') {
        // React Native or server environment - return default palette
        console.warn('Color extraction not available in this environment, using default palette');
        return getDefaultPalette();
    }
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    resolve(getDefaultPalette());
                    return;
                }
                // Scale down image for faster processing
                const scale = Math.min(100 / img.width, 100 / img.height);
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const colors = extractDominantColors(imageData.data);
                resolve(colors);
            }
            catch (error) {
                console.warn('Error extracting colors from image:', error);
                resolve(getDefaultPalette());
            }
        };
        img.onerror = () => {
            console.warn('Failed to load image for color extraction');
            resolve(getDefaultPalette());
        };
        img.src = imageUrl;
    });
});
exports.extractColorsFromImage = extractColorsFromImage;
/**
 * Extract dominant colors from image data
 */
const extractDominantColors = (data) => {
    const colorCounts = {};
    const step = 4; // RGBA
    // Sample every 4th pixel for performance
    for (let i = 0; i < data.length; i += step * 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];
        // Skip transparent pixels
        if (a < 128)
            continue;
        // Skip very light or very dark pixels
        const brightness = (r + g + b) / 3;
        if (brightness < 30 || brightness > 225)
            continue;
        // Quantize colors to reduce noise
        const quantizedR = Math.floor(r / 32) * 32;
        const quantizedG = Math.floor(g / 32) * 32;
        const quantizedB = Math.floor(b / 32) * 32;
        const colorKey = `${quantizedR},${quantizedG},${quantizedB}`;
        colorCounts[colorKey] = (colorCounts[colorKey] || 0) + 1;
    }
    // Get most frequent colors
    const sortedColors = Object.entries(colorCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([color]) => color.split(',').map(Number));
    if (sortedColors.length === 0) {
        return getDefaultPalette();
    }
    // Select primary, secondary, and accent colors
    const primary = sortedColors[0];
    const secondary = findComplementaryColor(sortedColors, primary);
    const accent = findAccentColor(sortedColors, primary, secondary);
    return {
        primary: `rgb(${primary[0]}, ${primary[1]}, ${primary[2]})`,
        secondary: `rgb(${secondary[0]}, ${secondary[1]}, ${secondary[2]})`,
        accent: `rgb(${accent[0]}, ${accent[1]}, ${accent[2]})`
    };
};
/**
 * Find a complementary color that contrasts well with the primary
 */
const findComplementaryColor = (colors, primary) => {
    let bestColor = colors[1] || primary;
    let maxDistance = 0;
    for (const color of colors.slice(1)) {
        const distance = colorDistance(primary, color);
        if (distance > maxDistance) {
            maxDistance = distance;
            bestColor = color;
        }
    }
    return bestColor;
};
/**
 * Find an accent color that works well with primary and secondary
 */
const findAccentColor = (colors, primary, secondary) => {
    let bestColor = colors[2] || secondary;
    let maxScore = 0;
    for (const color of colors.slice(2)) {
        const primaryDistance = colorDistance(primary, color);
        const secondaryDistance = colorDistance(secondary, color);
        const score = Math.min(primaryDistance, secondaryDistance);
        if (score > maxScore) {
            maxScore = score;
            bestColor = color;
        }
    }
    return bestColor;
};
/**
 * Calculate color distance using Euclidean distance
 */
const colorDistance = (color1, color2) => {
    const dr = color1[0] - color2[0];
    const dg = color1[1] - color2[1];
    const db = color1[2] - color2[2];
    return Math.sqrt(dr * dr + dg * dg + db * db);
};
/**
 * Get default color palette for fallback
 */
const getDefaultPalette = () => ({
    primary: 'rgb(125, 122, 255)',
    secondary: 'rgb(54, 52, 163)',
    accent: 'rgb(255, 122, 125)'
});
/**
 * Create gradient background from color palette
 */
const createGradientFromPalette = (palette, isDarkMode) => {
    if (isDarkMode) {
        return `linear-gradient(135deg, 
      ${palette.primary}15 0%, 
      ${palette.secondary}20 50%, 
      ${palette.accent}10 100%)`;
    }
    else {
        return `linear-gradient(135deg, 
      ${palette.primary}25 0%, 
      ${palette.secondary}30 50%, 
      ${palette.accent}20 100%)`;
    }
};
exports.createGradientFromPalette = createGradientFromPalette;
/**
 * Create a more subtle gradient for glassy effects
 */
const createSubtleGradientFromPalette = (palette, isDarkMode) => {
    if (isDarkMode) {
        return `linear-gradient(135deg, 
      ${palette.primary}08 0%, 
      ${palette.secondary}12 50%, 
      ${palette.accent}06 100%)`;
    }
    else {
        return `linear-gradient(135deg, 
      ${palette.primary}12 0%, 
      ${palette.secondary}18 50%, 
      ${palette.accent}10 100%)`;
    }
};
exports.createSubtleGradientFromPalette = createSubtleGradientFromPalette;
